import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconCircleX } from '@tabler/icons-react';
import { useState } from 'react';
import { ApiError } from '../api/client';
import {
  useApplyIsolation,
  usePreviewIsolation,
  type IsolationBlock,
  type IsolationPreview,
  type OrgDto,
} from '../api/orgs';

interface Props {
  org: OrgDto;
}

function normalise(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

function formatApplyError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Validation failed. Check the inputs and run Validate again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
    if (err.status === 422)
      return 'Validation failed. The supplied values were rejected by the server.';
  }
  return fallback;
}

function FieldValue({ value }: { value: string | null }) {
  if (value === null) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        global default
      </Text>
    );
  }
  return <Text size="sm">{value}</Text>;
}

function ValidationResult({
  result,
}: {
  result: IsolationPreview['validation'][keyof IsolationPreview['validation']];
}) {
  if (result.ok) {
    return (
      <Group gap={4}>
        <IconCircleCheck size={14} color="green" />
        <Text size="xs" c="green">
          {result.detail || 'OK'}
        </Text>
      </Group>
    );
  }
  return (
    <Group gap={4}>
      <IconCircleX size={14} color="red" />
      <Text size="xs" c="red">
        {result.detail}
      </Text>
    </Group>
  );
}

interface EditModalProps {
  org: OrgDto;
  isolation: IsolationBlock;
  opened: boolean;
  onClose: () => void;
}

function IsolationEditModal({ org, isolation, opened, onClose }: EditModalProps) {
  const preview = usePreviewIsolation(org.id);
  const apply = useApplyIsolation(org.id);

  const [dbConnectionRef, setDbConnectionRef] = useState(isolation.dbConnectionRef ?? '');
  const [storageBucket, setStorageBucket] = useState(isolation.storageBucket ?? '');
  const [kmsKeyArn, setKmsKeyArn] = useState(isolation.kmsKeyArn ?? '');
  const [confirmName, setConfirmName] = useState('');
  const [previewResult, setPreviewResult] = useState<IsolationPreview | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Track the input snapshot that was last validated so we know if the
  // Apply button refers to the current preview result.
  const [validatedSnapshot, setValidatedSnapshot] = useState<{
    dbConnectionRef: string;
    storageBucket: string;
    kmsKeyArn: string;
  } | null>(null);

  const currentSnapshot = { dbConnectionRef, storageBucket, kmsKeyArn };
  const snapshotMatchesPreview =
    validatedSnapshot !== null &&
    validatedSnapshot.dbConnectionRef === dbConnectionRef &&
    validatedSnapshot.storageBucket === storageBucket &&
    validatedSnapshot.kmsKeyArn === kmsKeyArn;

  const allOk =
    previewResult !== null &&
    previewResult.validation.dbConnectionRef.ok &&
    previewResult.validation.storageBucket.ok &&
    previewResult.validation.kmsKeyArn.ok;

  const isDestructive = previewResult?.destructive ?? false;
  const confirmNameMatch = isDestructive ? confirmName === org.name : true;

  const applyEnabled = snapshotMatchesPreview && allOk && confirmNameMatch;

  function handleValidate() {
    setApplyError(null);
    const input = {
      dbConnectionRef: normalise(dbConnectionRef),
      storageBucket: normalise(storageBucket),
      kmsKeyArn: normalise(kmsKeyArn),
    };
    preview.mutate(input, {
      onSuccess: (result) => {
        setPreviewResult(result);
        setValidatedSnapshot(currentSnapshot);
        setConfirmName('');
      },
    });
  }

  function handleApply() {
    setApplyError(null);
    const input = {
      dbConnectionRef: normalise(dbConnectionRef),
      storageBucket: normalise(storageBucket),
      kmsKeyArn: normalise(kmsKeyArn),
      ...(isDestructive ? { confirmOrgName: confirmName } : {}),
    };
    apply.mutate(input, {
      onSuccess: () => onClose(),
      onError: (err) => setApplyError(formatApplyError(err, 'Failed to apply isolation settings.')),
    });
  }

  function handleClose() {
    // Reset local state when closing so it's fresh next open.
    setDbConnectionRef(isolation.dbConnectionRef ?? '');
    setStorageBucket(isolation.storageBucket ?? '');
    setKmsKeyArn(isolation.kmsKeyArn ?? '');
    setConfirmName('');
    setPreviewResult(null);
    setValidatedSnapshot(null);
    setApplyError(null);
    onClose();
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Edit clinical isolation"
      size="md"
      transitionProps={{ duration: 0 }}
    >
      <Stack gap="md">
        <TextInput
          label="Clinical DB connection ref"
          value={dbConnectionRef}
          onChange={(e) => setDbConnectionRef(e.currentTarget.value)}
          disabled={preview.isPending || apply.isPending}
        />
        {previewResult && snapshotMatchesPreview && (
          <ValidationResult result={previewResult.validation.dbConnectionRef} />
        )}

        <TextInput
          label="Storage bucket"
          value={storageBucket}
          onChange={(e) => setStorageBucket(e.currentTarget.value)}
          disabled={preview.isPending || apply.isPending}
        />
        {previewResult && snapshotMatchesPreview && (
          <ValidationResult result={previewResult.validation.storageBucket} />
        )}

        <TextInput
          label="KMS key ARN"
          value={kmsKeyArn}
          onChange={(e) => setKmsKeyArn(e.currentTarget.value)}
          disabled={preview.isPending || apply.isPending}
        />
        {previewResult && snapshotMatchesPreview && (
          <ValidationResult result={previewResult.validation.kmsKeyArn} />
        )}

        {isDestructive && snapshotMatchesPreview && previewResult && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              {previewResult.impact.orphanedCaseCount} cases and{' '}
              {previewResult.impact.orphanedPatientCount} patients are on this organisation's
              current database and will NOT be migrated — they will be orphaned.
            </Text>
          </Alert>
        )}

        {isDestructive && snapshotMatchesPreview && (
          <TextInput
            label="Type the organisation name to confirm"
            value={confirmName}
            onChange={(e) => setConfirmName(e.currentTarget.value)}
            disabled={apply.isPending}
          />
        )}

        {applyError && (
          <Alert color="red" icon={<IconAlertTriangle size={16} />}>
            {applyError}
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={apply.isPending}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleValidate}
            loading={preview.isPending}
            disabled={apply.isPending}
          >
            Validate
          </Button>
          <Button onClick={handleApply} disabled={!applyEnabled} loading={apply.isPending}>
            Apply
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * "Clinical isolation" card. Renders on the org detail page. Shows the
 * current isolation status and lets an admin configure per-org clinical
 * DB, storage bucket, and KMS key settings via a validated preview/apply
 * workflow.
 */
export function IsolationConfigCard({ org }: Props) {
  const [editOpen, setEditOpen] = useState(false);

  const archived = Boolean(org.deletedAt);
  const isolation = org.isolation ?? {
    status: 'shared' as const,
    dbConnectionRef: null,
    storageBucket: null,
    kmsKeyArn: null,
  };

  return (
    <Card withBorder padding="lg" maw={680}>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={5}>Clinical isolation</Title>
          {isolation.status === 'isolated' ? (
            <Badge color="orange">Isolated</Badge>
          ) : (
            <Badge color="gray" variant="light">
              Shared
            </Badge>
          )}
        </Group>

        <Stack gap="xs">
          <Group gap="xs">
            <Text size="sm" fw={500} w={180}>
              Clinical DB connection ref:
            </Text>
            <FieldValue value={isolation.dbConnectionRef} />
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500} w={180}>
              Storage bucket:
            </Text>
            <FieldValue value={isolation.storageBucket} />
          </Group>
          <Group gap="xs">
            <Text size="sm" fw={500} w={180}>
              KMS key ARN:
            </Text>
            <FieldValue value={isolation.kmsKeyArn} />
          </Group>
        </Stack>

        <Group>
          <Button variant="default" disabled={archived} onClick={() => setEditOpen(true)}>
            Edit isolation
          </Button>
        </Group>
      </Stack>

      <IsolationEditModal
        org={org}
        isolation={isolation}
        opened={editOpen}
        onClose={() => setEditOpen(false)}
      />
    </Card>
  );
}
